import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import MathRender from "@/components/Math";
import { ArrowLeft, ArrowRight, Compass, Layers, PlayCircle, Sparkles } from "lucide-react";

// Same accent map as Topics.jsx (kept local for scope isolation)
const ACCENT_TEXT = {
  pink: "text-pink-300", orange: "text-orange-300", yellow: "text-yellow-300",
  lime: "text-lime-300", emerald: "text-emerald-300", sky: "text-sky-300",
  cyan: "text-cyan-300", violet: "text-violet-300", fuchsia: "text-fuchsia-300",
  rose: "text-rose-300", amber: "text-amber-300", teal: "text-teal-300",
};
const ACCENT_BORDER = {
  pink: "border-pink-300/40", orange: "border-orange-300/40", yellow: "border-yellow-300/40",
  lime: "border-lime-300/40", emerald: "border-emerald-300/40", sky: "border-sky-300/40",
  cyan: "border-cyan-300/40", violet: "border-violet-300/40", fuchsia: "border-fuchsia-300/40",
  rose: "border-rose-300/40", amber: "border-amber-300/40", teal: "border-teal-300/40",
};
const ACCENT_BG = {
  pink: "bg-pink-300/10", orange: "bg-orange-300/10", yellow: "bg-yellow-300/10",
  lime: "bg-lime-300/10", emerald: "bg-emerald-300/10", sky: "bg-sky-300/10",
  cyan: "bg-cyan-300/10", violet: "bg-violet-300/10", fuchsia: "bg-fuchsia-300/10",
  rose: "bg-rose-300/10", amber: "bg-amber-300/10", teal: "bg-teal-300/10",
};

// Course-id → track label. Only Class 10 has separate tracks.
function trackLabel(courseId, difficulty) {
  if (!difficulty) return null;
  return difficulty === "standard" ? "041 · Standard" : "241 · Basic";
}

export default function TopicDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/learn/topics/${slug}`).then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="text-zinc-500 text-sm" data-testid="topic-detail-loading">Loading topic…</div>;
  if (!data) return <div className="text-red-300 text-sm">Topic not found.</div>;

  const t = data.topic;
  const accentText = ACCENT_TEXT[t.accent] || "text-pink-300";
  const accentBorder = ACCENT_BORDER[t.accent] || "border-pink-300/40";
  const accentBg = ACCENT_BG[t.accent] || "bg-pink-300/10";
  const hasReadyPractice = data.practice_ready_units > 0;

  return (
    <div data-testid="topic-detail-page">
      <Link
        to="/topics"
        className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-yellow-300 mb-4"
        data-testid="topic-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> All topics
      </Link>

      <div className="mb-6 fade-up">
        <div className={`text-[10px] uppercase tracking-[0.2em] mb-1 ${accentText}`}>Topic</div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">{t.title}</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl text-sm">{t.tagline}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-mono text-zinc-500">
          <span>{data.unit_count} unit{data.unit_count === 1 ? "" : "s"}</span>
          <span className="text-zinc-700">·</span>
          <span>{data.grade_groups.length} class{data.grade_groups.length === 1 ? "" : "es"} covered</span>
          {hasReadyPractice && (
            <>
              <span className="text-zinc-700">·</span>
              <span className={accentText}>{data.practice_ready_units} practice-ready</span>
            </>
          )}
        </div>
      </div>

      {/* Deep-dive intro — the "why this matters" hook */}
      {t.deep_dive && (
        <div
          data-testid="topic-deep-dive"
          className={`fade-up mb-8 rounded-lg border ${accentBorder} bg-gradient-to-br from-white/[0.02] to-transparent p-5 md:p-6`}
        >
          <div className={`text-[10px] uppercase tracking-[0.2em] mb-3 flex items-center gap-1.5 ${accentText}`}>
            <Compass className="w-3 h-3" /> Why this matters
          </div>
          <div
            className={`font-display text-lg md:text-xl leading-snug mb-3 ${accentText}`}
            data-testid="topic-deep-dive-hook"
          >
            <MathRender>{t.deep_dive.hook}</MathRender>
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed mb-4 max-w-2xl">
            <MathRender>{t.deep_dive.why_it_matters}</MathRender>
          </p>
          {t.deep_dive.arc?.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
                The story arc
              </div>
              <ol className="space-y-1.5" data-testid="topic-deep-dive-arc">
                {t.deep_dive.arc.map(([grade, note], i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                    <span className={`font-mono text-[11px] w-16 shrink-0 pt-0.5 ${accentText}`}>
                      {grade}
                    </span>
                    <span className="text-zinc-300">{note}</span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}

      {/* Topic-scoped practice paper CTA */}
      {hasReadyPractice ? (
        <Link
          to={`/topics/${slug}/practice`}
          data-testid="topic-practice-cta"
          className={`fade-up flex items-center justify-between rounded-lg border ${accentBorder} ${accentBg} p-5 mb-8 hover:brightness-125 transition-all group`}
        >
          <div>
            <div className={`text-[10px] uppercase tracking-[0.2em] mb-1 ${accentText}`}>
              Topic-wise practice paper
            </div>
            <div className="font-display text-lg font-semibold text-zinc-100">
              Mixed practice — all classes, one paper
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              15 hand-picked questions spanning every class where this topic appears.
              Auto-scores with hint ladder + AI Tutor when you're stuck.
            </div>
          </div>
          <PlayCircle className={`w-8 h-8 ${accentText} group-hover:scale-110 transition-transform`} />
        </Link>
      ) : (
        <div className="fade-up rounded-lg border border-orange-300/25 bg-orange-300/5 p-4 mb-8 flex items-center gap-3">
          <Sparkles className="w-4 h-4 text-orange-300 shrink-0" />
          <div className="text-sm text-zinc-200">
            <span className="text-orange-300 font-semibold">Practice bank in progress</span>{" "}
            for this topic — the units below are still browsable, and topic
            practice unlocks once questions are seeded.
          </div>
        </div>
      )}

      {/* Grade-grouped unit list */}
      <div className="space-y-8" data-testid="topic-grade-groups">
        {data.grade_groups.map((g) => {
          const multiTrack = g.courses.length > 1;
          return (
            <section key={g.grade} data-testid={`topic-grade-${g.grade}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="font-display text-base text-zinc-300">Class {g.grade}</div>
                <div className="h-px flex-1 bg-white/5" />
                <div className="text-[10px] font-mono text-zinc-500">
                  {g.units.length} unit{g.units.length === 1 ? "" : "s"}
                  {multiTrack && ` · ${g.courses.length} tracks`}
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.units.map((u, i) => (
                  <Link
                    key={u.id}
                    to={`/learn/unit/${u.id}`}
                    data-testid={`topic-unit-${u.id}`}
                    className="fade-up lift rounded-lg border border-white/10 bg-[#152040] p-4 flex flex-col group hover:border-yellow-300/40 transition-colors"
                    style={{ animationDelay: `${(i % 6) * 40}ms` }}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[10px] font-mono text-zinc-500">
                        Unit {u.number}
                      </div>
                      {trackLabel(u.course_id, u.course_difficulty) && (
                        <div className="text-[9px] uppercase tracking-[0.15em] text-zinc-500 border border-white/10 rounded-full px-1.5 py-0.5">
                          {trackLabel(u.course_id, u.course_difficulty)}
                        </div>
                      )}
                    </div>
                    <div className="font-display text-base font-semibold leading-snug mb-2 text-zinc-100">
                      {u.title}
                    </div>
                    <div className="text-xs text-zinc-500 line-clamp-2 flex-1 min-h-[32px]">
                      {u.summary}
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                      {u.practice_bank_ready === false ? (
                        <span className="text-[10px] uppercase tracking-[0.15em] text-orange-300 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Soon
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-300 flex items-center gap-1">
                          <Layers className="w-3 h-3" /> Practice ready
                        </span>
                      )}
                      <ArrowRight className="w-3.5 h-3.5 text-yellow-300 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
